<template>
    <Dialog :open="isOpen" @close="closeHandler">
        <form
            :class="['base-confirm-backdrop', {open: isOpen}]"
            @submit.prevent="emits('accept')"
        >
            <DialogPanel
                :class="['base-confirm', {contentType: isContentType}]"
                v-bind="$attrs"
            >
                <div class="base-confirm-wrap">
                    <DialogTitle
                        v-if="title"
                        :class="'base-confirm-title'"
                        as="section"
                    >
                        <h2>{{ title }}</h2>

                        <CloseSvg
                            v-if="isContentType"
                            class="close-btn"
                            @click="emits('cancel')"
                        />
                    </DialogTitle>

                    <div class="base-confirm-content">
                        <div
                            class="base-confirm-description"
                            v-if="message"
                            v-html="message"
                        ></div>

                        <div class="base-confirm-description" v-else>
                            <slot></slot>
                        </div>

                        <section
                            class="base-confirm-footer"
                            :class="[
                                buttonsPosition == 'center' ? 'center' : null,
                                footerSectionClassName
                                    ? `${footerSectionClassName}`
                                    : null,
                            ]"
                        >
                            <!--
                                type을 지정하지 않으면 <button>의 기본값이 submit이라
                                이 취소 버튼이 form 안의 "첫 submit 버튼"이 된다.
                                그 상태에서 input에 포커스를 두고 Enter를 누르면
                                브라우저의 implicit submission이 취소를 눌러 팝업이 닫힌다.
                            -->
                            <CustomButton
                                v-if="isShowCancelBtn"
                                type="button"
                                color="secondary"
                                size="large"
                                @click="emits('cancel')"
                            >
                                {{ cancelText }}</CustomButton
                            >
                            <slot name="footerButton"></slot>
                            <CustomButton
                                v-if="isShowAcceptBtn"
                                size="large"
                                :disabled="acceptBtnDisabled"
                                @submit="emits('accept')"
                            >
                                {{ confirmText }}</CustomButton
                            >
                        </section>
                    </div>
                </div>
            </DialogPanel>
        </form>
    </Dialog>
</template>

<script setup lang="ts">
import CloseSvg from '@/assets/svg/icn_close.svg?component';
import CustomButton from '@/components/common/input/CustomButton.vue';
import {Dialog, DialogPanel, DialogTitle} from '@headlessui/vue';
import {PropType, toRefs, watch} from 'vue';

type buttonsPositionT = 'right' | 'center';

const emits = defineEmits<{
    (event: 'accept'): void;
    (event: 'cancel'): void;
    (event: 'show'): void; // 모달 보여지기전에 호출 됨
}>();

const props = defineProps({
    isOpen: {type: Boolean, required: true},
    title: {type: String, default: ''},
    message: {type: String, default: ''},
    confirmText: {type: String, default: '확인'},
    cancelText: {type: String, default: '닫기'},
    ignoreBackdropClick: {type: Boolean, default: false},
    buttonsPosition: {
        type: String as PropType<buttonsPositionT>,
        default: 'right',
    },
    acceptBtnDisabled: {type: Boolean, default: false},
    isShowCancelBtn: {type: Boolean, default: true},
    isShowAcceptBtn: {type: Boolean, default: true},
    isContentType: {type: Boolean, default: false},
    footerSectionClassName: {type: String},
});

const {ignoreBackdropClick} = toRefs(props);

const closeHandler = () => {
    if (!ignoreBackdropClick.value) {
        emits('cancel');
    }
};

/**
 * @description BaseConfirm의 show 핸들링 로직
 * BaseConfirm이 오픈되는 시점에 실행할 로직을 show에 전달합니다.
 * 예: BaseConfirm 오픈 시, API 요청 전송
 */
watch(
    () => props.isOpen,
    (newVal) => {
        if (newVal === true) {
            // NOTE: isOpen의 업데이트된 값이 true일때만 show 이벤트 발동
            emits('show');
            return;
        } else {
            // NOTE: 그외의 케이스에는 show 이벤트 발동하지 않으며, BaseConfirm 닫음
            closeHandler();
        }
    },
    {
        immediate: true,
    },
);
</script>

<style lang="scss" scoped>
.base-confirm-backdrop {
    position: fixed;
    top: 0;
    height: 0;
    width: 100vw;
    height: 100vh;
    background: $black_32;
    z-index: $page-modal-z-index;
    overflow-x: hidden;
    overflow-y: auto;
}

.base-confirm {
    margin: 20px auto;
    width: 335px;

    min-height: calc(100% - (20px * 2));
    display: flex;
    align-items: center;
    z-index: $page-modal-z-index;

    @media (max-width: 500px) {
        width: 95% !important;
    }
    .base-confirm-wrap {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        min-width: fit-content;
        background: $white;
        border-radius: 10px;
        color: $black_80;
        word-break: break-all;
        font-size: 16px;
        line-height: 24px;
        padding: 16px 20px 0;

        .base-confirm-title {
            display: flex;
            justify-content: space-between;
            > h2 {
                padding: 13px 0 8px;
                color: $NDS20;
                font-size: 18px;
                font-weight: bold;
                line-height: 22px;
                font-weight: 700;
            }
        }

        .close-btn {
            :deep() {
                path {
                    fill: $NDS20;
                }
            }
        }

        .base-confirm-description {
            margin-top: 8px;
        }

        .base-confirm-footer {
            display: flex;
            justify-content: flex-end;
            column-gap: 8px;
            margin: 20px 0;

            > button {
                flex: 1;
            }

            &__point-request-detail-view-popup {
                // 포인트 지급/회수요청 상세내역 조회 팝업용
                flex-direction: row-reverse;
                justify-content: end;
            }

            &.center {
                justify-content: center;
            }
        }
    }

    &.contentType {
        margin: 20px auto;
        min-width: 335px;
        width: fit-content;
        min-height: calc(100% - (20px * 2));
        display: flex;
        align-items: center;
        z-index: $page-modal-z-index;

        .base-confirm-wrap {
            padding: 0;
            border-radius: 16px;
            color: $black_80;
            word-break: break-all;
            font-size: 14px;
            line-height: 24px;

            .base-confirm-title {
                border-radius: 16px 16px 0 0;
                display: flex;
                justify-content: space-between;
                padding: 18px 24px;
                background: $bg-modalhead;
                border-bottom: 1px solid $ele-divider;
                align-items: center;
                font-size: 20px;
                line-height: 28px;
                > h2 {
                    padding: 0;
                }

                &__close-btn {
                    fill: $black_64;
                    cursor: pointer;

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }

            .base-confirm-content {
                padding: 24px 24px 0;
            }

            .base-confirm-footer {
                > button {
                    flex: none;
                }
                justify-content: flex-end;
                margin: 24px 0;
            }
        }
    }
}

.v-enter-active,
.v-leave-active {
    transition: all 2s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
